import {
  PropsWithChildren,
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import type { AppMode, SessionResponseDto } from "@madamuh/domain";
import { ApiError } from "../api/client";
import { createRefreshRequestGate, type RefreshRequestGate } from "./commercialAccessRefresh";
import {
  fetchCurrentSession,
  patchMemberMode,
  postLineLogin,
  postMemberInviteConsume,
  postOwnerOnboardingConsume,
  postStudentSelfInviteConsume
} from "../api/session";

type SessionBootstrapStatus = "idle" | "loading" | "authenticated" | "unauthenticated" | "error";
type SessionRefreshResult =
  | { status: "authenticated"; session: SessionResponseDto }
  | { status: "unauthenticated" }
  | { status: "error"; errorMessage: string };

type SessionBootstrapContextValue = {
  status: SessionBootstrapStatus;
  session: SessionResponseDto | null;
  sessionReceivedAtMs: number | null;
  errorMessage: string | null;
  bootstrapSession: (tenantSlug: string) => Promise<SessionResponseDto | null>;
  verifySession: (tenantSlug: string) => Promise<SessionRefreshResult>;
  refreshSessionSilently: (tenantSlug: string) => Promise<SessionRefreshResult>;
  switchAppMode: (tenantSlug: string, appMode: AppMode) => Promise<SessionResponseDto>;
  establishSessionWithLineToken: (tenantSlug: string, idToken: string) => Promise<SessionResponseDto>;
  consumeOwnerOnboardingToken: (tenantSlug: string, token: string) => Promise<SessionResponseDto>;
  consumeMemberInviteToken: (tenantSlug: string, token: string) => Promise<SessionResponseDto>;
  consumeStudentSelfInviteToken: (tenantSlug: string, token: string) => Promise<SessionResponseDto>;
  lineLoginWithMockToken: (tenantSlug: string, idToken: string) => Promise<SessionResponseDto | null>;
  resetBootstrapState: () => void;
};

const SessionBootstrapContext = createContext<SessionBootstrapContextValue | null>(null);
const DEFAULT_VERIFY_DELAYS_MS = [0, 150, 350];
const POST_LOGIN_VERIFY_DELAYS_MS = [0, 150, 350, 750, 1500, 2500];

function isSessionForTenant(session: SessionResponseDto, tenantSlug: string): boolean {
  return session.tenant.tenantSlug === tenantSlug;
}

function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, milliseconds);
  });
}

class SessionCookieEstablishmentError extends Error {
  constructor() {
    super("Session cookie could not be established");
    this.name = "SessionCookieEstablishmentError";
  }
}

export function SessionBootstrapProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<SessionBootstrapStatus>("idle");
  const [session, setSession] = useState<SessionResponseDto | null>(null);
  const [sessionReceivedAtMs, setSessionReceivedAtMs] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const sessionRef = useRef<SessionResponseDto | null>(null);
  const mountedRef = useRef(true);
  const requestGateRef = useRef<RefreshRequestGate | null>(null);
  if (requestGateRef.current === null) requestGateRef.current = createRefreshRequestGate();
  const requestGate = requestGateRef.current;

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestGate.invalidate();
    };
  }, [requestGate]);

  const applyAuthenticatedSession = useCallback((ticket: number, nextSession: SessionResponseDto) => {
    if (!mountedRef.current || !requestGate.isCurrent(ticket)) return false;
    sessionRef.current = nextSession;
    setSession(nextSession);
    setSessionReceivedAtMs(Date.now());
    setStatus("authenticated");
    setErrorMessage(null);
    return true;
  }, [requestGate]);

  const applyUnauthenticated = useCallback((ticket: number) => {
    if (!mountedRef.current || !requestGate.isCurrent(ticket)) return false;
    sessionRef.current = null;
    setSession(null);
    setSessionReceivedAtMs(null);
    setStatus("unauthenticated");
    setErrorMessage(null);
    return true;
  }, [requestGate]);

  const verifySessionWithDelays = useCallback(async (
    tenantSlug: string,
    delayScheduleMs: number[],
    existingTicket?: number
  ): Promise<SessionRefreshResult> => {
    const ticket = existingTicket ?? requestGate.beginForeground();
    let lastUnauthorizedError: ApiError | null = null;

    for (const delayMs of delayScheduleMs) {
      if (delayMs > 0) {
        await sleep(delayMs);
      }
      if (!mountedRef.current || !requestGate.isCurrent(ticket)) {
        const current = sessionRef.current;
        return current && isSessionForTenant(current, tenantSlug)
          ? { status: "authenticated", session: current }
          : { status: "error", errorMessage: "Session verification was superseded" };
      }

      try {
        const nextSession = await fetchCurrentSession();

        if (!mountedRef.current || !requestGate.isCurrent(ticket)) {
          const current = sessionRef.current;
          return current && isSessionForTenant(current, tenantSlug)
            ? { status: "authenticated", session: current }
            : { status: "error", errorMessage: "Session verification was superseded" };
        }

        if (!isSessionForTenant(nextSession, tenantSlug)) {
          applyUnauthenticated(ticket);
          return { status: "unauthenticated" };
        }

        applyAuthenticatedSession(ticket, nextSession);
        return {
          status: "authenticated",
          session: nextSession
        };
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          lastUnauthorizedError = error;
          continue;
        }

        const nextErrorMessage = error instanceof Error ? error.message : "Session bootstrap failed";
        if (mountedRef.current && requestGate.isCurrent(ticket)) {
          sessionRef.current = null;
          setSession(null);
          setSessionReceivedAtMs(null);
          setStatus("error");
          setErrorMessage(nextErrorMessage);
        }
        return {
          status: "error",
          errorMessage: nextErrorMessage
        };
      }
    }

    if (lastUnauthorizedError) {
      if (!mountedRef.current || !requestGate.isCurrent(ticket)) {
        const current = sessionRef.current;
        return current && isSessionForTenant(current, tenantSlug)
          ? { status: "authenticated", session: current }
          : { status: "error", errorMessage: "Session verification was superseded" };
      }
      applyUnauthenticated(ticket);
      return { status: "unauthenticated" };
    }

    const nextErrorMessage = "Session bootstrap failed";
    if (mountedRef.current && requestGate.isCurrent(ticket)) {
      sessionRef.current = null;
      setSession(null);
      setSessionReceivedAtMs(null);
      setStatus("error");
      setErrorMessage(nextErrorMessage);
    }
    return {
      status: "error",
      errorMessage: nextErrorMessage
    };
  }, [applyAuthenticatedSession, applyUnauthenticated, requestGate]);

  const verifySession = useCallback(async (
    tenantSlug: string
  ): Promise<SessionRefreshResult> => {
    setStatus("loading");
    setErrorMessage(null);
    return verifySessionWithDelays(tenantSlug, DEFAULT_VERIFY_DELAYS_MS);
  }, [verifySessionWithDelays]);

  const refreshSessionSilently = useCallback(async (tenantSlug: string): Promise<SessionRefreshResult> => {
    const gated = await requestGate.runSilent(tenantSlug, async (): Promise<SessionRefreshResult> => {
      try {
        const nextSession = await fetchCurrentSession();
        return isSessionForTenant(nextSession, tenantSlug)
          ? { status: "authenticated", session: nextSession }
          : { status: "unauthenticated" };
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return { status: "unauthenticated" };
        return {
          status: "error",
          errorMessage: error instanceof Error ? error.message : "Session refresh failed"
        };
      }
    });
    if (gated.stale || !mountedRef.current || !requestGate.isCurrent(gated.ticket)) {
      const current = sessionRef.current;
      return current && isSessionForTenant(current, tenantSlug)
        ? { status: "authenticated", session: current }
        : { status: "error", errorMessage: "Session refresh was superseded" };
    }
    const result = gated.value;
    if (result.status === "authenticated") {
      applyAuthenticatedSession(gated.ticket, result.session);
    } else if (result.status === "unauthenticated") {
      applyUnauthenticated(gated.ticket);
    } else if (sessionRef.current) {
      setStatus("authenticated");
      setErrorMessage(result.errorMessage);
    }
    return result;
  }, [applyAuthenticatedSession, applyUnauthenticated, requestGate]);

  const bootstrapSession = useCallback(async (tenantSlug: string): Promise<SessionResponseDto | null> => {
    const result = await verifySession(tenantSlug);
    return result.status === "authenticated" ? result.session : null;
  }, [verifySession]);

  const resetBootstrapState = useCallback(() => {
    requestGate.invalidate();
    sessionRef.current = null;
    setSession(null);
    setSessionReceivedAtMs(null);
    setStatus("idle");
    setErrorMessage(null);
  }, [requestGate]);

  const establishSessionWithLineToken = useCallback(async (tenantSlug: string, idToken: string): Promise<SessionResponseDto> => {
    const ticket = requestGate.beginForeground();
    setStatus("loading");
    setErrorMessage(null);

    try {
      await postLineLogin({ tenantSlug, idToken });
      const verifiedSession = await verifySessionWithDelays(tenantSlug, POST_LOGIN_VERIFY_DELAYS_MS, ticket);

      if (verifiedSession.status === "authenticated") {
        return verifiedSession.session;
      }

      if (verifiedSession.status === "error") {
        throw new Error(verifiedSession.errorMessage);
      }

      throw new SessionCookieEstablishmentError();
    } catch (error) {
      if (!mountedRef.current || !requestGate.isCurrent(ticket)) {
        throw error instanceof Error ? error : new Error("LINE login failed");
      }
      sessionRef.current = null;
      setSession(null);
      setSessionReceivedAtMs(null);

      if (error instanceof SessionCookieEstablishmentError) {
        setStatus("unauthenticated");
        setErrorMessage(error.message);
        throw error;
      }

      setStatus("error");
      setErrorMessage(error instanceof Error ? error.message : "LINE login failed");
      throw error instanceof Error ? error : new Error("LINE login failed");
    }
  }, [requestGate, verifySessionWithDelays]);

  const switchAppMode = useCallback(async (tenantSlug: string, appMode: AppMode): Promise<SessionResponseDto> => {
    const ticket = requestGate.beginForeground();
    setStatus("loading");
    setErrorMessage(null);

    try {
      const nextSession = await patchMemberMode(tenantSlug, appMode);
      applyAuthenticatedSession(ticket, nextSession);
      return nextSession;
    } catch (error) {
      if (mountedRef.current && requestGate.isCurrent(ticket)) {
        setStatus(sessionRef.current ? "authenticated" : "error");
        setErrorMessage(error instanceof Error ? error.message : "App mode switch failed");
      }
      throw error instanceof Error ? error : new Error("App mode switch failed");
    }
  }, [applyAuthenticatedSession, requestGate]);

  const consumeOwnerOnboardingToken = useCallback(async (
    tenantSlug: string,
    token: string
  ): Promise<SessionResponseDto> => {
    const ticket = requestGate.beginForeground();
    setStatus("loading");
    setErrorMessage(null);

    try {
      const consumedSession = await postOwnerOnboardingConsume(tenantSlug, token);
      const verifiedSession = await verifySessionWithDelays(tenantSlug, [0, 50, 150], ticket);

      if (verifiedSession.status === "authenticated") {
        return verifiedSession.session;
      }

      applyAuthenticatedSession(ticket, consumedSession);
      return consumedSession;
    } catch (error) {
      if (mountedRef.current && requestGate.isCurrent(ticket)) {
        setStatus(sessionRef.current ? "authenticated" : "error");
        setErrorMessage(error instanceof Error ? error.message : "Owner onboarding consume failed");
      }
      throw error instanceof Error ? error : new Error("Owner onboarding consume failed");
    }
  }, [applyAuthenticatedSession, requestGate, verifySessionWithDelays]);

  const consumeMemberInviteToken = useCallback(async (
    tenantSlug: string,
    token: string
  ): Promise<SessionResponseDto> => {
    const ticket = requestGate.beginForeground();
    setStatus("loading");
    setErrorMessage(null);

    try {
      const consumedSession = await postMemberInviteConsume(tenantSlug, token);
      const verifiedSession = await verifySessionWithDelays(tenantSlug, [0, 50, 150], ticket);

      if (verifiedSession.status === "authenticated") {
        return verifiedSession.session;
      }

      applyAuthenticatedSession(ticket, consumedSession);
      return consumedSession;
    } catch (error) {
      if (mountedRef.current && requestGate.isCurrent(ticket)) {
        setStatus(sessionRef.current ? "authenticated" : "error");
        setErrorMessage(error instanceof Error ? error.message : "Member invite consume failed");
      }
      throw error instanceof Error ? error : new Error("Member invite consume failed");
    }
  }, [applyAuthenticatedSession, requestGate, verifySessionWithDelays]);

  const consumeStudentSelfInviteToken = useCallback(async (
    tenantSlug: string,
    token: string
  ): Promise<SessionResponseDto> => {
    const ticket = requestGate.beginForeground();
    setStatus("loading");
    setErrorMessage(null);

    try {
      const consumedSession = await postStudentSelfInviteConsume(tenantSlug, token);
      applyAuthenticatedSession(ticket, consumedSession);
      return consumedSession;
    } catch (error) {
      if (mountedRef.current && requestGate.isCurrent(ticket)) {
        setStatus(sessionRef.current ? "authenticated" : "error");
        setErrorMessage(error instanceof Error ? error.message : "Student self invite consume failed");
      }
      throw error instanceof Error ? error : new Error("Student self invite consume failed");
    }
  }, [applyAuthenticatedSession, requestGate]);

  const lineLoginWithMockToken = useCallback(async (tenantSlug: string, idToken: string): Promise<SessionResponseDto | null> => {
    try {
      return await establishSessionWithLineToken(tenantSlug, idToken);
    } catch {
      return null;
    }
  }, [establishSessionWithLineToken]);

  return (
    <SessionBootstrapContext.Provider
      value={{
        status,
        session,
        sessionReceivedAtMs,
        errorMessage,
        bootstrapSession,
        verifySession,
        refreshSessionSilently,
        switchAppMode,
        establishSessionWithLineToken,
        consumeOwnerOnboardingToken,
        consumeMemberInviteToken,
        consumeStudentSelfInviteToken,
        lineLoginWithMockToken,
        resetBootstrapState
      }}
    >
      {children}
    </SessionBootstrapContext.Provider>
  );
}

export function useSessionBootstrap() {
  const context = useContext(SessionBootstrapContext);

  if (!context) {
    throw new Error("useSessionBootstrap must be used within SessionBootstrapProvider");
  }

  return context;
}
